import React, { useState, useEffect } from 'react';
import ApiService from '../services/api';
import ComplaintService from '../services/complaintService';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import dashboardService from '../services/dashboardService';
import authService from '../services/authService';
import {
  Search,
  Eye,
  UserCheck,
  FileText,
  AlertTriangle,
  Upload,
  MessageCircle,
  Send,
  Paperclip,
  X,
  Image,
  File,
  Printer,
  ChevronLeft,
  ChevronRight,
  Inbox,
  Download,
  Loader,
  CheckCircle,
  Clock,
} from 'lucide-react';
import DashboardHeader from '../components/DashboardHeader';
import AssignComplaintPage from '../components/AssignComplaint/AssignComplaintPage';
import complaintService from '../services/complaintService';
import { getMediaUrl } from '../config/apiConfig';
import TranslatedText from '../components/TranslatedText';
import { useTranslation } from 'react-i18next';
// Mock complaints data - moved outside component to avoid re-creation on every render
const mockComplaints = [
  {
    id: 'CMPXYZ123',
    name: 'John Doe',
    mobile: '9876543210',
    address: '123 Main Street, Andheri East, Mumbai - 400069',
    village: 'Andheri East',
    description:
      'No water supply for the past 3 days in our building. The main water tank seems to be empty and the pump is not working. This is affecting 50+ families in the building.',
    status: 'In Progress',
    submittedDate: '2024-01-15',
    assignedTo: 'Water Department',
    priority: 'High',
    officerNotes: 'Field inspection completed. Pump motor needs replacement.',
    timeline: [
      {
        date: '2024-01-15',
        action: 'Complaint Submitted',
        officer: 'System',
        note: 'Auto-assigned to Water Department',
      },
      {
        date: '2024-01-16',
        action: 'Under Review',
        officer: 'R. Kumar',
        note: 'Reviewed and accepted for field inspection',
      },
      {
        date: '2024-01-17',
        action: 'Field Inspection',
        officer: 'R. Kumar',
        note: 'Visited site. Pump motor requires replacement.',
      },
    ],
  },
  {
    id: 'CMPABC456',
    name: 'Jane Smith',
    mobile: '9123456789',
    address: '456 Park Road, Borivali West, Mumbai - 400092',
    village: 'Borivali West',
    description:
      'Large pothole causing traffic issues and vehicle damage. The pothole is approximately 3 feet wide and 1 foot deep.',
    status: 'Resolved',
    submittedDate: '2024-01-10',
    assignedTo: 'PWD',
    priority: 'Medium',
    officerNotes: 'Pothole filled with concrete mix. Road marking completed.',
    timeline: [
      {
        date: '2024-01-10',
        action: 'Complaint Submitted',
        officer: 'System',
        note: 'Auto-assigned to PWD',
      },
      {
        date: '2024-01-11',
        action: 'Assessment',
        officer: 'S. Patil',
        note: 'Site inspection completed',
      },
      {
        date: '2024-01-14',
        action: 'Resolved',
        officer: 'S. Patil',
        note: 'Pothole repair completed successfully',
      },
    ],
  },
];

//convert inpute date to backend format
function toBackendDate(dateStr, endOfDay = false) {
  if (!dateStr) return '';
  return endOfDay ? `${dateStr} 23:59:59.999999` : `${dateStr} 00:00:00.000000`;
}

// Parse submittedDate from API or local data into a Date object for comparisons.
// Handles formats like '2025-09-22 15:51:38', '2025-09-22', and Date objects.
function parseSubmittedDate(dateVal) {
  if (!dateVal) return null;
  if (dateVal instanceof Date) return dateVal;
  if (typeof dateVal === 'string') {
    // try full parse first
    const asDate = new Date(dateVal);
    if (!isNaN(asDate.getTime())) return asDate;
    // fallback: take date-only part (before space or T)
    const dateOnly = dateVal.split(' ')[0].split('T')[0];
    const d2 = new Date(dateOnly);
    return isNaN(d2.getTime()) ? null : d2;
  }
  // try generic construction
  const d = new Date(dateVal);
  return isNaN(d.getTime()) ? null : d;
}

// Convert English digits to Devanagari script
function toDevanagariDigits(num) {
  const devanagariDigits = '०१२३४५६७८९';
  return num.toString().replace(/\d/g, (digit) => devanagariDigits[digit]);
}

function DashboardPage() {
  const { user: authUser, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const [complaints, setComplaints] = useState([]);
  const [filteredComplaints, setFilteredComplaints] = useState([]);
  const [filters, setFilters] = useState({
    status: 'ALL STATUS',
    dateFrom: '',
    dateTo: '',
    raisedByCollector: false,
    raisedByCitizen: false,
  });
  const [reportFilters, setReportFilters] = useState({
    dateFrom: '',
    dateTo: '',
    department: '',
    taluka: '',
    category: '',
    status: '',
    search: '',
  });
  const [selectedTimeLine, setSelectedTimeLine] = useState([]);
  const [totalComplaints, setTotalComplaints] = useState(0);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignComplaint, setAssignComplaint] = useState(null);
  const [showResolutionModal, setShowResolutionModal] = useState(false);
  const [resolutionComplaint, setResolutionComplaint] = useState(null);
  const [resolutionComment, setResolutionComment] = useState('');
  const [resolutionFiles, setResolutionFiles] = useState([]);
  const [resolvingComplaint, setResolvingComplaint] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  const goToFirstPage = () => goToPage(1);
  const goToLastPage = () => goToPage(totalPages);

  // New state for enhanced modal features
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'comments', 'files'
  const [newComment, setNewComment] = useState('');
  const [commentType, setCommentType] = useState('PUBLIC'); // 'public' or 'internal'
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [comments, setComments] = useState([]);
  const [attachmentPreviews, setAttachmentPreviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    pending: 0,
    inProgress: 0,
    resolved: 0,
    rejected: 0,
    averageTat: 0,
  });
  // Add this function to your component
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-100 text-red-800 border border-red-200';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      case 'LOW':
        return 'bg-green-100 text-green-800 border border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  // Convert numbers based on language - returns Devanagari digits for Marathi, English digits otherwise
  const formatNumberForLanguage = (num, lang) => {
    if (!num && num !== 0) return '';
    if (lang === 'mr') {
      return toDevanagariDigits(num);
    }
    return num.toString();
  };

  // Format file size with Devanagari digits for Marathi
  const formatFileSizeWithLanguage = (sizeInBytes) => {
    if (!sizeInBytes) return 'Unknown size';
    let sizeStr = '';
    if (sizeInBytes < 1024 * 1024) {
      sizeStr = `${(sizeInBytes / 1024).toFixed(1)} KB`;
    } else {
      sizeStr = `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
    }
    return formatNumberForLanguage(sizeStr, currentLanguage);
  };

  const user = authService.getCurrentUser();
  const departmentName = user?.departmentName;

  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
  };

  const handleSave = () => {


  };





  // Handle back button with logout confirmation
  useEffect(() => {
    const handleBackButton = (event) => {
      event.preventDefault();
      const confirmLogout = window.confirm(t('dashboard.common.logoutConfirm'));
      if (confirmLogout) {
        logout();
      } else {
        // Stay on current page
        window.history.pushState(null, '', window.location.pathname);
      }
    };

    // Add current entry to history stack
    window.history.pushState(null, '', window.location.pathname);

    // Listen for popstate (back button)
    window.addEventListener('popstate', handleBackButton);

    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [logout]);


  const loadFromApi = async () => {
    try {
      const response = await ComplaintService.getComplaints({
        page: filters.status === 'PENDING' ? 0 : currentPage - 1,
        size: filters.status === 'PENDING' ? 2000 : itemsPerPage,
        status:
          filters.status !== 'ALL STATUS' ? filters.status : undefined,
        departmentId: filters.departmentId,
        taluka: filters.taluka,

        dateFrom: toBackendDate(filters.dateFrom, false),
        dateTo: toBackendDate(filters.dateTo, true),
        search: reportFilters.search.trim(),
        raisedBy:
          filters.raisedByCollector && !filters.raisedByCitizen
            ? 'COLLECTOR'
            : !filters.raisedByCollector && filters.raisedByCitizen
              ? 'CITIZEN'
              : undefined,
      });

      const items = Array.isArray(response?.content)
        ? response.content
        : [];
      const data = response.data || response;
      const normalized = normalizeComplaintData(data.content || []);

      setComplaints(normalized || []);
      setTotalPages(filters.status === 'PENDING' ? 1 : (data.totalPages || 0));
      setTotalComplaints(data.totalElements || 0);


    } catch (e) {
      console.warn('API error fetching complaints:', e);
      // Check if it's a session expiration error
      if (e.message && e.message.includes('Session expired')) {
        console.warn('Session expired, user will be redirected to login');
        // Don't fall back to mock data for expired sessions
        return;
      }
      // For other errors, fall back to mock data
      console.warn('Falling back to mock complaints due to API error');
      setComplaints(mockComplaints);
      // setFilteredComplaints(mockComplaints);
    }
  };

  useEffect(() => {
    if (authUser) {
      const mapStatus = (s) => {
        if (!s) return 'New';
        switch (s.toUpperCase()) {
          case 'NEW':
            return 'New';
          case 'PENDING':
            return 'Pending';
          case 'ASSIGNED':
            return 'Assigned';
          case 'IN_PROGRESS':
            return 'In Progress';
          case 'RESOLVED':
            return 'Resolved';
          case 'REJECTED':
            return 'Rejected';
          case 'REOPEN':
          case 'REOPENED':
            return 'Reopen';
          default:
            return s;
        }
      };

      const mapPriority = (p) => {
        if (!p) return 'Medium';
        const up = p.toUpperCase();
        if (up === 'CRITICAL') return 'Critical';
        if (up === 'HIGH') return 'High';
        if (up === 'MEDIUM') return 'Medium';
        if (up === 'LOW') return 'Low';
        return p;
      };



      loadFromApi();

      // Auto-refresh complaints every 30 seconds
      const intervalId = setInterval(() => {
        loadFromApi();
      }, 30000);

      // Cleanup interval on unmount or when dependencies change
      return () => clearInterval(intervalId);
    }
  }, [
    authUser,
    filters,
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder,
    reportFilters.search,
  ]);


  const normalizeComplaintData = (data) => {
    if (!Array.isArray(data)) return [];

    return data.map((item) => ({
      id: item.id,
      complaintId: item.complaintId,
      name: item.name,
      mobile: item.mobile,
      village: item.village,
      address: item.address,
      taluka: item.taluka,
      departmentName: item.departmentName,
      description: item.description,
      status: item.status,
      priority: item.priority,
      submittedDate: formatDateTime(item.submittedDate),
      assignedDate: item.assignedDate ? formatDateTime(item.assignedDate) : null,
      raisedBy: item.raisedBy,
      assignedTo: item.assignedTo,
      assignedBy: item.assignedBy,
      officerNotes: item.officerNotes,
      email: item.email,
      address: item.address,
      resolvedDate: item.resolvedDate ? formatDateTime(item.resolvedDate) : null,
      pendingDays: item.pendingDays,
      timeline: item.timeline?.map((t) => ({
        date: formatDateTime(t.date),
        status: t.status,
        description: t.description,
        action: t.action,
        officerName: t.officerName || "",
      })),
      comments: item.comments?.map((c) => ({
        id: c.id,
        comment: c.comment,
        by: c.commentBy,
        type: c.commenterType,
        visibility: c.visibility,
        createdAt: formatDateTime(c.createdAt),
      })),

      attachments_officer: item.attachments_officer?.map((a) => ({
        complaint_code: a.complaint_code,
        fileName: a.fileName,
        fileType: a.fileType,
        id: a.id,
        size: a.size,
        uploadedAt: formatDateTime(a.uploadedAt),
        url: a.url,

      })),
      attachments_citizen: item.attachments_citizen?.map((a) => ({
        complaint_code: a.complaint_code,
        fileName: a.fileName,
        fileType: a.fileType,
        id: a.id,
        size: a.size,
        uploadedAt: formatDateTime(a.uploadedAt),
        url: a.url,

      }))
    }));
  };



  {/* ✅ Date formatting helper */ }

  const formatDateTime = (dateString) => {
    if (!dateString) return null;

    let date;
    // Handle Array format [yyyy, mm, dd, hh, mm, ss]
    if (Array.isArray(dateString)) {
      const [year, month, day, hour = 0, minute = 0, second = 0] = dateString;
      // Construct ISO string with IST offset to treat components as IST
      const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}+05:30`;
      date = new Date(iso);
    } else {
      // Handle String format
      let isoString = dateString.replace(' ', 'T');
      // If no timezone indicator, assume IST (+05:30) instead of UTC (Z)
      if (!isoString.endsWith('Z') && !isoString.includes('+')) {
        isoString += '+05:30';
      }
      date = new Date(isoString);
    }

    // Invalid date check
    if (isNaN(date.getTime())) {
      return formatNumberForLanguage(dateString, currentLanguage);
    }

    // Use Intl.DateTimeFormat to force Asia/Kolkata timezone
    const options = {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };

    try {
      const formatter = new Intl.DateTimeFormat('en-IN', options);
      const parts = formatter.formatToParts(date);
      const getPart = (type) => parts.find(p => p.type === type)?.value;

      const day = getPart('day');
      const month = getPart('month');
      const year = getPart('year');
      const hour = getPart('hour');
      const minute = getPart('minute');
      const dayPeriod = getPart('dayPeriod');

      const dateTimeStr = `${day}/${month}/${year} ${hour}:${minute} ${dayPeriod.toUpperCase()}`;
      return formatNumberForLanguage(dateTimeStr, 'en');
    } catch (e) {
      console.error('Date formatting error:', e);
      return date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
    }
  };


  useEffect(() => {
    let filtered = complaints;

    // Apply filters
    if (filters.status && filters.status !== 'ALL STATUS') {
      filtered = filtered.filter((complaint) => {
        const s = (complaint.status || '').toUpperCase();
        const f = filters.status.toUpperCase();
        
        // Specific handling for Reopen status to prevent partial matching
        if (f === 'REOPEN' || f === 'REOPENED') {
          return s === 'REOPEN' || s === 'REOPENED';
        }
        
        // Standard comparison with normalization
        const sNorm = s.replace(/\s+/g, '_');
        const fNorm = f.replace(/\s+/g, '_');

        return sNorm === fNorm;
      });
    }
    if (filters.category) {
      filtered = filtered.filter(
        (complaint) => complaint.category === filters.category
      );
    }




    // Search functionality
    if (reportFilters.search) {
      const query = reportFilters.search.toLowerCase();
      filtered = filtered.filter(
        (complaints) =>
          complaints.id?.toLowerCase().includes(query) ||
          complaints.name?.toLowerCase().includes(query) ||
          complaints.mobile?.toLowerCase().includes(query) ||
          complaints.village?.toLowerCase().includes(query) ||
          complaints.taluka?.toLowerCase().includes(query) ||
          complaints.status?.toLowerCase().includes(query) ||
          complaints.priority?.toLowerCase().includes(query) ||
          complaints.submittedDate?.toLowerCase().includes(query) ||
          complaints.resolvedDate?.toLowerCase().includes(query) ||
          // ✅ Added Address & Description Search (User Request)
          complaints.address?.toLowerCase().includes(query) ||
          complaints.description?.toLowerCase().includes(query)
      );
    }

    //sorting with COLLECTOR complaints prioritized at the top
    // Filter by Raised By
    if (filters.raisedByCollector) {
      filtered = filtered.filter((complaint) => complaint.raisedBy === 'COLLECTOR');
    }
    if (filters.raisedByCitizen) {
      filtered = filtered.filter((complaint) => complaint.raisedBy !== 'COLLECTOR');
    }

    // Sorting with COLLECTOR complaints prioritized at the top
    filtered.sort((a, b) => {
      const aIsCollector = a.raisedBy === 'COLLECTOR';
      const bIsCollector = b.raisedBy === 'COLLECTOR';

      // Step 1: Always keep COLLECTOR complaints at the top
      if (aIsCollector && !bIsCollector) return -1;
      if (!aIsCollector && bIsCollector) return 1;

      // Step 2: Within same group, apply your sortBy logic
      let aValue, bValue;

      switch (sortBy) {
        case 'date':
          aValue = new Date(a.rawSubmittedDate || a.submittedDate);
          bValue = new Date(b.rawSubmittedDate || b.submittedDate);
          // Always sort by latest first (descending)
          return bValue - aValue;

        case 'priority':
          const priorityOrder = { Critical: 4, High: 3, Medium: 2, Low: 1 };
          aValue = priorityOrder[a.priority] || 0;
          bValue = priorityOrder[b.priority] || 0;
          return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;

        case 'status':
          aValue = a.status?.toLowerCase() || '';
          bValue = b.status?.toLowerCase() || '';
          return sortOrder === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);

        default:
          aValue = a[sortBy];
          bValue = b[sortBy];
          if (sortOrder === 'asc') return aValue > bValue ? 1 : -1;
          else return aValue < bValue ? 1 : -1;
      }
    });    setFilteredComplaints(filtered);

    // Reset to first page when filters change
    // setCurrentPage(1);
  }, [complaints, filters, reportFilters.search, sortBy, sortOrder]);

  const handleFilterChange = (e) => {
    setFilters((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const STATUS_DISPLAY_MAP = {
    NEW: 'New',
    IN_PROGRESS: 'In Progress',
    RESOLVED: 'Resolved',
    REJECTED: 'Rejected',
    REOPEN: 'Reopen',
    PENDING: 'Pending',
  };

  const DISPLAY_TO_ENUM_MAP = {
    New: 'NEW',
    'In Progress': 'IN_PROGRESS',
    Resolved: 'RESOLVED',
    Rejected: 'REJECTED',
    Reopen: 'REOPEN',
    Pending: 'PENDING',
  };

  const handleStatusUpdate = async (complaintId, newStatus) => {
    // If trying to resolve, show resolution modal instead
    if (newStatus === 'RESOLVED') {
      const complaint =
        complaints.find((c) => c.id === complaintId) || selectedComplaint;
      setResolutionComplaint(complaint);
      setShowResolutionModal(true);
      return;
    }

    // If reopening, set status to IN_PROGRESS
    if (newStatus === 'REOPEN') {
      newStatus = 'IN_PROGRESS';
    }

    try {
      // Optimistically update UI using display name
      setComplaints((prev) =>
        prev.map((complaint) =>
          complaint.id === complaintId
            ? {
              ...complaint,
              status: newStatus,
            }
            : complaint
        )
      );

      // Keep modal in sync if it's the same complaint
      setSelectedComplaint((prev) =>
        prev && prev.id === complaintId
          ? { ...prev, status: STATUS_DISPLAY_MAP[newStatus] || newStatus }
          : prev
      );

      // Send enum-style status to API
      await ComplaintService.updateComplaintStatus(
        complaintId,
        newStatus, // API expects SUBMITTED, RESOLVED, etc.
        'Status updated by officer'
      );

      // } catch (error) {
      //   console.error('Failed to update status on server:', error);



      await getStatsCounts();

    } catch (error) {
      console.error('Failed to update status on server:', error);

      // Optionally revert UI change
      setComplaints((prev) =>
        prev.map((complaint) =>
          complaint.id === complaintId
            ? { ...complaint, status: complaint.status } // revert
            : complaint
        )
      );
    }
  };
  const handlePriorityUpdate = async (complaintId, priority) => {
    try {
      // Always await the API call
      const result = await dashboardService.updatePriority(complaintId, priority);

      // Check if backend returned a success response
      if (result?.success || result?.status === 200) {


        // Update selected complaint (modal view)
        setSelectedComplaint((prev) =>
          prev && prev.id === complaintId ? { ...prev, priority } : prev
        );

        // Update complaint list (table view)
        setComplaints((prev) =>
          prev.map((c) => (c.id === complaintId ? { ...c, priority } : c))
        );

        // Refresh page data
        await loadFromApi();
      } else {
        console.error('❌ Failed to update priority on server:', result);
      }
    } catch (error) {
      console.error('⚠️ Error updating priority on server:', error);
    }
  };



  const downloadFile = async (url, fileName, token) => {
    try {
      const response = await fetch(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined, // if your API is protected
        },
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error('Error downloading file:', err);
    }
  };

  const normalizeAttachmentEndpoint = (rawUrl) => {
    if (!rawUrl) return null;
    try {
      if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
        const parsed = new URL(rawUrl);
        const pathWithQuery = `${parsed.pathname}${parsed.search || ''}`;
        if (pathWithQuery.startsWith('/api')) {
          return pathWithQuery.replace('/api', '') || '/';
        }
        return pathWithQuery || '/';
      }
    } catch (_) { }
    let normalized = rawUrl.startsWith('/api') ? rawUrl.replace('/api', '') : rawUrl;
    if (!normalized.startsWith('/')) normalized = `/${normalized}`;
    return normalized;
  };

  // Handle viewing image in modal
  const handleViewImage = async (attachment, complaintId, isOfficer = false) => {
    try {
      const endpoint = normalizeAttachmentEndpoint(
        attachment.url || attachment.fileUrl || attachment.path
      );
      if (!endpoint) {
        alert('Unable to preview this attachment. Missing file path.');
        return;
      }

      const blob = await ApiService.getBlob(endpoint);
      const objectUrl = URL.createObjectURL(blob);

      const fileType = attachment.fileType || '';
      const ext = fileType.includes('/')
        ? fileType.split('/')[1]
        : 'bin';
      const filename = isOfficer
        ? `${complaintId}_resolution.${ext}`
        : `${complaintId}.${ext}`;

      // ✅ If it's a PDF, open it directly in a new tab
      if (fileType === 'application/pdf' || filename.toLowerCase().endsWith('.pdf')) {
        window.open(objectUrl, '_blank');
        return; // Stop here, don't show modal
      }

      // 🖼 For images and others — use modal
      setSelectedImage((prev) => {
        if (prev?.isObjectUrl && prev.url) {
          URL.revokeObjectURL(prev.url);
        }
        return {
          url: objectUrl,
          filename,
          attachment,
          downloadEndpoint: endpoint,
          isObjectUrl: true,
        };
      });
      setShowImageModal(true);
    } catch (error) {
      console.error('Error loading image:', error);
      alert('Error loading image. Please try again.');
    }
  };


  // Handle download from modal
  const handleDownloadFromModal = async () => {
    // 1. Get data from where the 'Eye' button stores it
    const data = selectedImage?.attachment || selectedImage;

    if (!data) {
      alert('No image data found.');
      return;
    }

    // 2. Locate the path. We check multiple names to ensure we don't get 'undefined'
    // In handleViewImage we set 'downloadEndpoint', so let's check that too.
    const rawPath = selectedImage.downloadEndpoint || data.url || data.fileUrl || data.path || data.filePath;

    if (!rawPath) {
      alert('Missing file path. Cannot download.');
      return;
    }

    // 3. Construct the clean URL (This removes 'apiundefined')
    const endpoint = normalizeAttachmentEndpoint(rawPath);

    try {
      const blob = await ApiService.getBlob(endpoint);
      const objectUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = objectUrl;

      // Determine filename
      // If selectedImage.filename is set, use it. Otherwise construct one.
      const filename = selectedImage.filename || `download.${data.fileType?.split('/')[1] || 'bin'}`;
      link.download = filename;

      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Download failed:', error);
      // If the Blob/API method fails, try a direct browser download as a last resort
      window.open(endpoint, '_blank');
    }
  };

  // Close image modal and cleanup
  const handleCloseImageModal = () => {
    // No need to revoke URL since we're using direct URLs, not blob URLs
    setShowImageModal(false);
    setSelectedImage(null);
  };

  // Handle complaint resolution with mandatory comment and optional files
  const handleResolveComplaint = async () => {
    if (!resolutionComment.trim()) {
      alert('Please add a closing comment before resolving the complaint.');
      return;
    }

    setResolvingComplaint(true);

    try {
      // First, add the resolution comment
      await ComplaintService.addComment(resolutionComplaint.id, {
        comment: resolutionComment.trim(),
        type: 'RESOLUTION',
      });

      // Upload resolution files if any


      // Update complaint status to resolved
      await ComplaintService.updateComplaintStatus(
        resolutionComplaint.id,
        'RESOLVED',
        resolutionComment.trim()
      );

      // Update UI - mark as resolved
      setComplaints((prev) =>
        prev.map((complaint) =>
          complaint.id === resolutionComplaint.id
            ? { ...complaint, status: 'Resolved' }
            : complaint
        )
      );

      // Update selected complaint if it's the same
      setSelectedComplaint((prev) =>
        prev && prev.id === resolutionComplaint.id
          ? { ...prev, status: 'Resolved' }
          : prev
      );
      await getStatsCounts();

      // } catch (error) {
      //   console.error('Failed to update status on server:', error);
      // Close modal and reset form
      setShowResolutionModal(false);
      setResolutionComplaint(null);
      setResolutionComment('');
      setResolutionFiles([]);


    } catch (error) {
      console.error('Error resolving complaint:', error);
      alert('Failed to resolve complaint. Please try again.');
    } finally {
      setResolvingComplaint(false);
    }
  };

  // Handle reopen complaint
  const handleReopenComplaint = async () => {
    if (!selectedComplaint) return;

    try {
      await ComplaintService.updateComplaintStatus(
        selectedComplaint.id,
        'IN_PROGRESS',
        'Complaint reopened by request'
      );

      // Update UI
      setComplaints((prev) =>
        prev.map((complaint) =>
          complaint.id === selectedComplaint.id
            ? { ...complaint, status: 'In Progress' }
            : complaint
        )
      );

      setSelectedComplaint((prev) => ({
        ...prev,
        status: 'In Progress',
      }));

      // alert('Complaint reopened successfully!');
    } catch (error) {
      console.error('Error reopening complaint:', error);
      alert('Failed to reopen complaint. Please try again.');
    }
  };

  const handleViewDetails = async (complaint) => {
    setSelectedComplaint(complaint);


    const reverseTimeLine = Array.isArray(complaint.timeline)
      ? [...complaint.timeline].reverse()
      : [];

    setSelectedTimeLine(reverseTimeLine);


    setShowDetailModal(true);


    try {
      const complaintId = complaint.id || complaint.complaintId;
      const apiComments = await ComplaintService.getComments(complaintId);
      setComments(Array.isArray(apiComments) ? apiComments : []);
    } catch (error) {
      setComments([]);
      console.error('Failed to fetch comments:', error);
    }
  };

  useEffect(() => {
    if (selectedComplaint) {

    }
  }, [selectedComplaint]);

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedComplaint(null);
    // Reset modal state
    setActiveTab('details');
    setNewComment('');
    setCommentType('PUBLIC');
    setSelectedFiles([]);
    setUploadProgress(0);
    setIsUploading(false);
    // Cleanup attachment object URLs
    setAttachmentPreviews((prev) => {
      prev.forEach((ap) => ap.objectUrl && URL.revokeObjectURL(ap.objectUrl));
      return [];
    });
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  // Remove selected file
  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };
  const formatStatus = (status) => {
    if (!status) return "";

    // Replace underscores with spaces, convert to lowercase, then capitalize each word
    return status
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };


  // Handle file upload
  const handleFileUpload = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        // Simulate upload progress
        for (let progress = 0; progress <= 100; progress += 20) {
          setUploadProgress((i * 100 + progress) / selectedFiles.length);
          await new Promise((resolve) => setTimeout(resolve, 200));
        }
      }

      // In real app, make API call to upload files


      // Add to timeline
      const newTimelineEntry = {
        action: 'Files Uploaded',
        note: `${selectedFiles.length} file(s) uploaded by officer`,
        officer: authUser?.name || 'Current Officer',
        date: new Date().toLocaleDateString(),
      };

      setSelectedComplaint((prev) => ({
        ...prev,
        timeline: [newTimelineEntry, ...prev.timeline],
      }));

      setSelectedFiles([]);
      alert('Files uploaded successfully!');
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle adding comment
  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedComplaint) return;

    const complaintId = selectedComplaint.id;
    const payload = {
      comment: newComment,
      commenterName: authUser?.name || 'Officer',
      visibility: commentType === 'INTERNAL' ? 'INTERNAL' : 'PUBLIC',
    };

    try {
      await complaintService.addComment(complaintId, payload);

      // Update local state as before
      const newCommentObj = {
        id: Date.now(),
        comment: newComment,
        visibility: commentType,
        commentBy: authUser?.name || 'Current Officer',
        createdAt: new Date().toLocaleString(),
      };

      setComments((prev) => [newCommentObj, ...prev]);

      // Add to timeline
      const timelineEntry = {
        action:
          commentType === 'INTERNAL'
            ? t('dashboard.comments.internalCommentAdded')
            : t('dashboard.comments.publicCommentAdded'),
        note: newComment,
        officer: authUser?.name || 'Current Officer',
        date: new Date().toLocaleDateString(),
      };

      setSelectedComplaint((prev) => ({
        ...prev,
        timeline: [timelineEntry, ...prev.timeline],
      }));

      setNewComment('');
    } catch (error) {
      alert('Failed to add comment. Please try again.');
      console.error(error);
    }
  };

  const handleUploadResolutionPhotos = async (complaintId) => {
    if (resolutionFiles.length === 0) {

      return;
    }

    const formData = new FormData();
    resolutionFiles.forEach((file) => formData.append('files', file));
    formData.append('complaintId', complaintId);

    try {
      const data = await ApiService.uploadFile('/files/upload', formData);


      setResolutionFiles([]); // clear after upload
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Error uploading files: ' + error.message);
    }
  };


  const handleAssignClick = (complaint) => {
    setAssignComplaint(complaint);
    setShowAssignModal(true);
  };

  // Modal close
  const handleAssignClose = () => {
    setShowAssignModal(false);
    setAssignComplaint(null);
  };

  // Refresh complaints data after assignment
  const handleAssignSuccess = async () => {
    try {
      // Close the modal first
      setShowAssignModal(false);
      setAssignComplaint(null);

      // Refresh complaints list AND stat counters together
      await Promise.all([loadFromApi(), getStatsCounts()]);
    } catch (error) {
      console.error('Error refreshing complaints after assignment:', error);
      // Still close the modal even if refresh fails
      setShowAssignModal(false);
      setAssignComplaint(null);
    }
  };


  // Handle download PDF to user's local directory
  const handleDownloadPDF = () => {
    // Create HTML content for PDF conversion
    const htmlContent = `
      <div style="font-family: Arial, sans-serif; padding: 20px; color: #333;">
        <div style="border-bottom: 2px solid #B8860B; margin-bottom: 20px; padding-bottom: 10px;">
          <h1 style="color: #B8860B; font-size: 24px; font-weight: bold; margin: 0;">Government Complaint Management System</h1>
          <p style="color: #666; margin: 5px 0;">Official Complaint Details Report</p>
        </div>

        <div style="margin-bottom: 20px;">
          <div style="font-weight: bold; color: #B8860B; margin-bottom: 10px; border-bottom: 1px solid #ddd; padding-bottom: 5px;">COMPLAINT INFORMATION</div>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 15px;">
            <div>
              <div style="margin-bottom: 8px;"><span style="font-weight: bold; color: #666;">Complaint ID:</span> ${selectedComplaint.complaintId}</div>

              <div style="margin-bottom: 8px;"><span style="font-weight: bold; color: #666;">Status:</span> ${selectedComplaint.status}</div>
              <div style="margin-bottom: 8px;"><span style="font-weight: bold; color: #666;">Priority:</span> ${selectedComplaint.priority}</div>
            </div>
            <div>
              <div style="margin-bottom: 8px;"><span style="font-weight: bold; color: #666;">Date Submitted:</span> ${selectedComplaint.submittedDate}</div>
              <div style="margin-bottom: 8px;"><span style="font-weight: bold; color: #666;">Assigned To:</span> ${selectedComplaint.assignedTo}</div>
              <div style="margin-bottom: 8px;"><span style="font-weight: bold; color: #666;">Download Date:</span> ${new Date().toLocaleDateString()}</div>
            </div>
          </div>
        </div>

        <div style="margin-bottom: 20px;">
          <div style="font-weight: bold; color: #B8860B; margin-bottom: 10px; border-bottom: 1px solid #ddd; padding-bottom: 5px;">CITIZEN DETAILS</div>
          <div style="margin-bottom: 8px;"><span style="font-weight: bold; color: #666;">Name:</span> ${selectedComplaint.name}</div>
          <div style="margin-bottom: 8px;"><span style="font-weight: bold; color: #666;">Mobile:</span> ${selectedComplaint.mobile}</div>
          <div style="margin-bottom: 8px;"><span style="font-weight: bold; color: #666;">Address:</span> ${selectedComplaint.address}</div>
          <div style="margin-bottom: 8px;"><span style="font-weight: bold; color: #666;">Village:</span> ${selectedComplaint.village}</div>
        </div>

        <div style="margin-bottom: 20px;">
          <div style="font-weight: bold; color: #B8860B; margin-bottom: 10px; border-bottom: 1px solid #ddd; padding-bottom: 5px;">COMPLAINT DESCRIPTION</div>
          <div style="background: #f9f9f9; padding: 10px; border-left: 4px solid #B8860B;">${selectedComplaint.description}</div>
        </div>

        ${selectedComplaint.assignmentReason
        ? `
        <div style="margin-bottom: 20px;">
          <div style="font-weight: bold; color: #B8860B; margin-bottom: 10px; border-bottom: 1px solid #ddd; padding-bottom: 5px;">ASSIGNMENT INFORMATION</div>
          <div style="background: #f9f9f9; padding: 10px; border-left: 4px solid #B8860B;">
            <div style="margin-bottom: 8px;"><span style="font-weight: bold; color: #666;">Assigned To:</span> ${selectedComplaint.assignedTo || 'N/A'}</div>
            <div style="margin-bottom: 8px;"><span style="font-weight: bold; color: #666;">Assignment Date:</span> ${selectedComplaint.assignedDate || 'N/A'}</div>
            <div><span style="font-weight: bold; color: #666;">Assignment Reason:</span> ${selectedComplaint.assignmentReason}</div>
          </div>
        </div>
        `
        : ''
      }
        ${selectedComplaint.officerNotes
        ? `
        <div style="margin-bottom: 20px;">
          <div style="font-weight: bold; color: #B8860B; margin-bottom: 10px; border-bottom: 1px solid #ddd; padding-bottom: 5px;">OFFICER NOTES</div>
          <div style="background: #f9f9f9; padding: 10px; border-left: 4px solid #B8860B;">${selectedComplaint.officerNotes}</div>
        </div>
        `
        : ''
      }

        <div style="margin-bottom: 20px;">
          <div style="font-weight: bold; color: #B8860B; margin-bottom: 10px; border-bottom: 1px solid #ddd; padding-bottom: 5px;">ACTION TIMELINE</div>
          ${selectedComplaint.timeline
        .map(
          (event) => `
            <div style="margin-bottom: 15px; padding-left: 15px; border-left: 2px solid #ddd;">
              <div style="font-weight: bold; color: #B8860B;">${event.date}</div>
              <div style="font-weight: bold; margin: 5px 0;">${event.action}</div>
              <div style="color: #666; margin: 5px 0;">${event.note}</div>
              <div style="font-size: 12px; color: #999;">By: ${event.officer}</div>
            </div>
          `
        )
        .join('')}
        </div>

        <div style="margin-top: 40px; text-align: center; color: #666; font-size: 12px;">
          <p>This is an official document generated from the Government Complaint Management System</p>
          <p>Generated on ${new Date().toLocaleString()}</p>
        </div>
      </div>
    `;

    // Create a blob with the HTML content
    const blob = new Blob(
      [
        `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Complaint_${selectedComplaint.complaintId}_Report</title>
          <meta charset="utf-8">
        </head>
        <body>
          ${htmlContent}
        </body>
      </html>
    `,
      ],
      { type: 'text/html' }
    );

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Complaint_${selectedComplaint.complaintId}_Report_${new Date().toISOString().split('T')[0]}.html`;

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    // Show user message
    alert('Complaint report has been downloaded to your Downloads folder!');
  };

  const handlePrint = () => {
    if (!selectedComplaint) {
      alert('No complaint selected for printing.');
      return;
    }

    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      alert('Popup blocked! Please allow popups for this site.');
      return;
    }

    const htmlContent = `
    <html>
    <head>
      <title>Complaint Details - ${selectedComplaint.complaintId}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
        .header { border-bottom: 2px solid #B8860B; margin-bottom: 20px; padding-bottom: 10px; }
        .title { color: #B8860B; font-size: 24px; font-weight: bold; margin: 0; }
        .subtitle { color: #666; margin: 5px 0; }
        .section { margin-bottom: 20px; }
        .section-title { font-weight: bold; color: #B8860B; margin-bottom: 10px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
        .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 15px; }
        .field { margin-bottom: 8px; }
        .field-label { font-weight: bold; color: #666; }
        .description { background: #f9f9f9; padding: 10px; border-left: 4px solid #B8860B; }
        .timeline-item { margin-bottom: 15px; padding-left: 15px; border-left: 2px solid #ddd; }
        .timeline-date { font-weight: bold; color: #B8860B; }
        .timeline-action { font-weight: bold; margin: 5px 0; }
        .timeline-note { color: #666; margin: 5px 0; }
        @media print {
          body { margin: 0; }
          .no-print { display: none !important; }
          .page-break { page-break-after: avoid; page-break-inside: avoid; }
        }
      </style>
    </head>
    <body>
      <div class="page-break">
        <div class="header">
          <h1 class="title">Government Complaint Management System</h1>
          <p class="subtitle">Official Complaint Details Report</p>
        </div>

        <div class="section">
          <div class="section-title">Complaint Information</div>
          <div class="grid">
            <div class="field"><span class="field-label">Complaint ID:</span> ${selectedComplaint.complaintId || 'N/A'}</div>

            <div class="field"><span class="field-label">Status:</span> ${formatStatus(selectedComplaint.status) || 'N/A'}</div>
            <div class="field"><span class="field-label">Priority:</span> ${selectedComplaint.priority || 'N/A'}</div>
            <div class="field"><span class="field-label">Date Submitted:</span> ${selectedComplaint.submittedDate || 'N/A'}</div>
            <div class="field"><span class="field-label">Assigned To:</span> ${selectedComplaint.assignedTo || 'N/A'}</div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Citizen Details</div>
          <div class="field"><span class="field-label">Name:</span> ${selectedComplaint.name || 'N/A'}</div>
          <div class="field"><span class="field-label">Mobile:</span> ${selectedComplaint.mobile || 'N/A'}</div>
          <div class="field"><span class="field-label">Address:</span> ${selectedComplaint.address || 'N/A'}</div>
          <div class="field"><span class="field-label">Village:</span> ${selectedComplaint.village || 'N/A'}</div>
        </div>

        <div class="section">
          <div class="section-title">Complaint Description</div>
          <div class="description">${selectedComplaint.description || 'N/A'}</div>
        </div>

        ${selectedComplaint.assignmentReason ? `
        <div class="section">
          <div class="section-title">Assignment Information</div>
          <div class="description">
            <p><strong>Assigned To:</strong> ${selectedComplaint.assignedTo || 'N/A'}</p>
            <p><strong>Assigned Date:</strong> ${selectedComplaint.assignedDate || 'N/A'}</p>
            <p><strong>Assignment Reason:</strong> ${selectedComplaint.assignmentReason}</p>
          </div>
        </div>
        ` : ''}

        ${selectedComplaint.officerNotes ? `
        <div class="section">
          <div class="section-title">Officer Notes</div>
          <div class="description">${selectedComplaint.officerNotes}</div>
        </div>
        ` : ''}

        <div class="section">
          <div class="section-title">Action Timeline</div>
          ${selectedComplaint.timeline && selectedComplaint.timeline.length > 0 ?
        selectedComplaint.timeline.map(event => `
              <div class="timeline-item">
                <div class="timeline-date">${event.date || ''}</div>
                <div class="timeline-action">${event.action || ''}</div>
                <div class="timeline-note">${event.note || ''}</div>
                <div style="font-size: 12px; color: #999;">By: ${event.officerName || selectedComplaint.raisedBy || "Citizen"}</div>
              </div>
            `).join('') :
        '<p>No timeline information available.</p>'
      }
        </div>

        <div style="margin-top:40px; text-align:center; font-size:12px; color:#666;">
          <p>This is an official document generated from the Government Complaint Management System</p>
          <p>Generated on ${new Date().toLocaleString()}</p>
        </div>
      </div>
    </body>
    </html>
  `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      // Optionally close the window after printing:
      // printWindow.close();
    };
  };



  useEffect(() => {
    const onKeyDown = (e) => {
      // Windows/Linux: Ctrl+P, macOS: Meta+P (Cmd+P)
      const isPrintShortcut =
        (e.ctrlKey && e.key.toLowerCase() === 'p') ||
        (e.metaKey && e.key.toLowerCase() === 'p');

      if (isPrintShortcut) {
        e.preventDefault();      // stop browser print dialog
        e.stopPropagation();     // optional: don’t bubble further
        handlePrint();           // your existing print logic
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handlePrint]);

  // Handle ESC key to close image modal
  useEffect(() => {
    if (!showImageModal) return;

    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        setShowImageModal(false);
        setSelectedImage(null);
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [showImageModal]);

  const formatStatusKeyVal = (status) => {
    if (!status) return '';
    const normalizedStatus = status.replace(/\s+/g, '_');
    const words = normalizedStatus.toLowerCase().split('_');
    return `status.${words[0]}${words.slice(1).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('')}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'NEW':
      case 'New':
        return 'bg-blue-100 text-blue-800';
      case 'IN_PROGRESS':
      case 'In Progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'RESOLVED':
      case 'Resolved':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
      case 'Pending':
        return 'bg-orange-100 text-orange-800';
      case 'REJECTED':
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      case 'REOPEN':
      case 'Reopen':
      case 'reopen':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Calculate days ago from submitted date
  const calculateDaysAgo = (submittedDate) => {
    if (!submittedDate) return '';

    try {
      // Handle different date formats
      let dateToCompare;

      // If it's already a Date object
      if (submittedDate instanceof Date) {
        dateToCompare = submittedDate;
      }
      // If it's a string like "2025-09-22 15:51:38" or "2025-09-22"
      else if (typeof submittedDate === 'string') {
        // Remove time portion if present and parse
        const Today = submittedDate.split(' ')[0];
        dateToCompare = new Date(Today);
      }
      // If it's a timestamp
      else {
        dateToCompare = new Date(submittedDate);
      }

      // Check if date is valid
      if (isNaN(dateToCompare.getTime())) {
        return '';
      }

      //     const today = new Date();
      //     const diffTime = Math.abs(today - dateToCompare);
      //     const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      //     if (diffDays === 0) {
      //       return '(Today)';
      //     } else if (diffDays === 1) {
      //       return '(Today)';
      //     } else {
      //       return `(${diffDays} Days ago)`;
      //     }
      //   } catch (error) {
      //     console.error('Error calculating days ago:', error);
      //     return '';
      //   }
      // };
      return '';
    } catch (error) {
      console.error('Error calculating days ago:', error);
      return '';
    }
  };

  const getStatsCounts = async () => {
    try {
      const response = await ComplaintService.getStats({

      });

      let averageTat = 0;
      if (['collector', 'admin'].includes(authUser?.role?.toLowerCase())) {
        try {
          const tatResponse = await ComplaintService.getAverageTat();
          averageTat = typeof tatResponse === 'object' && tatResponse !== null 
            ? (tatResponse.data?.averageTatDays ?? tatResponse.averageTatDays ?? tatResponse.data?.averageTat ?? tatResponse.averageTat ?? tatResponse.average ?? tatResponse.data ?? 0)
            : tatResponse;
          
          // Round to 2 decimal places if it's a number
          if (typeof averageTat === 'number') {
            averageTat = Math.round(averageTat * 100) / 100;
          }
        } catch (error) {
          console.error('Error fetching average TAT:', error);
        }
      }

      setStats({
        total: response.totalComplains || 0,
        new: response.totalNewComplaints || 0,
        pending: ['officer', 'taluka_officer', 'collector'].includes(authUser?.role?.toLowerCase())
          ? (response.totalPendingComplaints || 0)
          : ((response.totalComplains || 0) - (response.totalResolvedComplaints || 0) - (response.totalRejectedComplaints || 0)),
        inProgress: response.totalInProgressComplaints || 0,
        resolved: response.totalResolvedComplaints || 0,
        rejected: response.totalRejectedComplaints || 0,
        averageTat: typeof averageTat === 'number' ? averageTat : parseFloat(averageTat) || 0,
      });

    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    getStatsCounts();
  }, [filters, reportFilters]); // re-run when filters change


  // Load attachment blobs when a complaint is selected
  useEffect(() => {
    let isCancelled = false;

    // Cleanup previous object URLs before loading new ones
    setAttachmentPreviews((prev) => {
      prev.forEach((ap) => ap.objectUrl && URL.revokeObjectURL(ap.objectUrl));
      return [];
    });

    const loadAttachments = async () => {
      if (
        !selectedComplaint ||
        !Array.isArray(selectedComplaint.attachments_citizen) ||
        selectedComplaint.attachments_citizen.length === 0
      )
        return;


      try {
        const results = await Promise.all(
          selectedComplaint.attachments_citizen.map(async (att) => {
            try {
              // Normalize endpoint to avoid double /api prefix
              const normalizedEndpoint =
                att.url && att.url.startsWith('/api')
                  ? att.url.replace('/api', '')
                  : att.url || '';
              const blob = await ApiService.getBlob(normalizedEndpoint);
              const objectUrl = URL.createObjectURL(blob);



              return {
                id: att.id,
                fileName: att.fileName,
                fileType: att.fileType,
                size: att.size,
                objectUrl,
                complaint_code: selectedComplaint.complaintId,
              };
            } catch (e) {
              return {
                id: att.id,
                fileName: att.fileName,
                fileType: att.fileType,
                size: att.size,
                error: true,
                complaint_code: selectedComplaint.complaintId,
              };
            }
          })
        );
        if (!isCancelled) setAttachmentPreviews(results);
      } catch (_) { }
    };

    loadAttachments();

    return () => {
      isCancelled = true;
    };
  }, [selectedComplaint]);

  if (!authUser) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        {t('dashboard.common.pleaseLogin')}
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <DashboardHeader
        user={authUser}
        handleLogout={handleLogout}
        title={t('dashboard.title')}
        notificationCount={stats.submitted}
      />

      {/* Main Content */}
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        {/* Welcome Section */}
        <div className="mb-8">
          <div
            className="h-auto rounded-xl border border-slate-200 p-6 shadow-lg backdrop-blur-sm"
            style={{
              background:
                'linear-gradient(135deg, rgba(226, 168, 32, 0.84) 0%, rgba(255,230,140,0.9) 100%)',
            }}
          >
            <h2 className="mb-2 text-2xl font-bold text-gray-900">

              {t('dashboard.welcome.user')} {
                i18n.language === 'mr'
                  ? (authUser?.departmentNameMarathi || user?.departmentNameMarathi || authUser?.departmentName || user?.departmentName || t('dashboard.common.user'))
                  : (authUser?.departmentName || user?.departmentName || t('dashboard.common.user'))
              }

            </h2>
            <p className="mb-4 text-gray-600">
              {authUser?.role === 'officer' &&
                t('dashboard.welcome.manageComplaints')}
              {authUser?.role === 'taluka_officer' &&
                t('dashboard.welcome.overseeComplaints')}
              {authUser?.role === 'collector' &&
                t('dashboard.welcome.monitorComplaints')}
            </p>

            {/* Quick Stats */}
            <div className={`mb-8 mt-8 grid grid-cols-2 gap-4 ${authUser?.role === 'collector' ? 'md:grid-cols-4 lg:grid-cols-7' : 'md:grid-cols-6'}`}>
              {/* Total Complaints */}
              <div
                className="rounded-xl border border-gray-200/30 p-4 shadow-lg backdrop-blur-sm"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(255,251,235,0.95) 100%)',
                }}
              >
                <div className="flex items-center">
                  <FileText className="h-8 w-8 text-gray-600" />
                  <div className="ml-3">
                    <p className="text-2xl font-bold text-gray-900">
                      {formatNumberForLanguage(stats.total, currentLanguage)}
                    </p>
                    <p className="text-xs text-gray-500">{t('dashboard.stats.total')}</p>
                  </div>
                </div>
              </div>

              {/* New */}
              <div
                className="rounded-xl border border-blue-200/30 p-4 shadow-lg backdrop-blur-sm"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(239,246,255,0.95) 100%)',
                }}
              >
                <div className="flex items-center">
                  <Inbox className="h-8 w-8" style={{ color: '#3B82F6' }} />
                  <div className="ml-3">
                    <p
                      className="text-2xl font-bold"
                      style={{ color: '#3B82F6' }}
                    >
                      {formatNumberForLanguage(stats.new, currentLanguage)}
                    </p>
                    <p className="text-xs text-gray-500">{t('status.new')}</p>
                  </div>
                </div>
              </div>

              {/* In Progress */}
              <div
                className="rounded-xl border border-yellow-200/30 p-4 shadow-lg backdrop-blur-sm"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(254,249,195,0.95) 100%)',
                }}
              >
                <div className="flex items-center">
                  <Loader className="h-8 w-8" style={{ color: '#EAB308' }} />
                  <div className="ml-3">
                    <p
                      className="text-2xl font-bold"
                      style={{ color: '#EAB308' }}
                    >
                      {formatNumberForLanguage(stats.inProgress, currentLanguage)}
                    </p>
                    <p className="text-xs text-gray-500">{t('status.inProgress')}</p>
                  </div>
                </div>
              </div>

              {/* Resolved */}
              <div
                className="rounded-xl border border-green-200/30 p-4 shadow-lg backdrop-blur-sm"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(236,253,245,0.95) 100%)',
                }}
              >
                <div className="flex items-center">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                  <div className="ml-3">
                    <p className="text-2xl font-bold text-green-600">
                      {formatNumberForLanguage(stats.resolved, currentLanguage)}
                    </p>
                    <p className="text-xs text-gray-500">{t('status.resolved')}</p>
                  </div>
                </div>
              </div>

              {/* Pending */}
              <div
                className="rounded-xl border border-orange-200/30 p-4 shadow-lg backdrop-blur-sm"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(255,237,213,0.95) 100%)',
                }}
              >
                <div className="flex items-center">
                  <AlertTriangle
                    className="h-8 w-8"
                    style={{ color: '#EA580C' }}
                  />
                  <div className="ml-3">
                    <p
                      className="text-2xl font-bold"
                      style={{ color: '#EA580C' }}
                    >
                      {formatNumberForLanguage(stats.pending, currentLanguage)}
                    </p>
                    <p className="text-xs text-gray-500">{t('status.pending')}</p>
                  </div>
                </div>
              </div>

              {/* Rejected */}
              <div
                className="rounded-xl border border-red-200/30 p-4 shadow-lg backdrop-blur-sm"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(254,242,242,0.95) 100%)',
                }}
              >
                <div className="flex items-center">
                  <X className="h-8 w-8 text-red-600" />
                  <div className="ml-3">
                    <p className="text-2xl font-bold text-red-600">
                      {formatNumberForLanguage(stats.rejected, currentLanguage)}
                    </p>
                    <p className="text-xs text-gray-500">{t('status.rejected')}</p>
                  </div>
                </div>
              </div>

              {/* Average TAT */}
              {['collector', 'admin'].includes(authUser?.role?.toLowerCase()) && (
                <div
                  className="rounded-xl border border-purple-200/30 p-4 shadow-lg backdrop-blur-sm"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(243,232,255,0.95) 100%)',
                  }}
                >
                  <div className="flex items-center">
                    <Clock className="h-8 w-8 text-purple-600" />
                    <div className="ml-3">
                      <p className="text-2xl font-bold text-purple-600">
                        {formatNumberForLanguage(stats.averageTat, currentLanguage)}
                      </p>
                      <p className="text-xs text-gray-500"><TranslatedText>Average TAT (Days)</TranslatedText></p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Complaints Management Section */}
        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-lg">
          <div className="border-b border-gray-100 bg-white px-8 py-6">
            <h3 className="text-2xl font-bold text-gray-900">
              {t('admin.section.complaintsManagement')}
            </h3>
            <p className="mt-2 text-gray-600">
              {t('header.tagline')}
            </p>
          </div>

          {/* Search and Filters */}
          <div className="border-b border-gray-100 bg-gray-50/30 p-8">
            <div className="mb-6">
              <div className="relative max-w-2xl">
                <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400" />
                <input
                  type="text"
                  value={reportFilters.search}
                  onChange={(e) =>
                    setReportFilters((prev) => ({
                      ...prev,
                      search: e.target.value,
                    }))
                  }
                  placeholder={t('admin.search.placeholder')}
                  className="w-full rounded-xl border border-gray-200 bg-white py-4 pl-12 pr-4 text-sm shadow-sm transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
            </div>

            <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="appearance-none rounded-xl border border-gray-200 bg-white py-3 pl-4 pr-10 text-sm shadow-sm transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="ALL STATUS">{t('status.allStatus')}</option>
                <option value="NEW">{t('status.new')}</option>
                <option value="IN_PROGRESS">{t('status.inProgress')}</option>
                <option value="RESOLVED">{t('status.resolved')}</option>
                <option value="REJECTED">{t('status.rejected')}</option>
                <option value="REOPEN">{t('status.reopened')}</option>
                <option value="PENDING">{t('status.pending')}</option>
              </select>

              <input
                type="date"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                max={new Date().toISOString().split('T')[0]}
                placeholder={t('filters.dateFrom')}
                className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm shadow-sm transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              />

              <input
                type="date"
                name="dateTo"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                max={new Date().toISOString().split('T')[0]}
                placeholder={t('filters.dateTo')}
                className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm shadow-sm transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              />
            </div>

            {/* Interactive Filter Legend */}
            <div className="mb-6 flex select-none items-center gap-4 text-xs text-gray-600">
              <div
                onClick={() =>
                  setFilters((prev) => ({
                    ...prev,
                    raisedByCollector: !prev.raisedByCollector,
                    raisedByCitizen: !prev.raisedByCollector ? false : prev.raisedByCitizen,
                  }))
                }
                className={`flex items-center gap-1.5 cursor-pointer px-2 py-1 rounded transition-all border ${filters.raisedByCollector
                  ? 'bg-purple-100 border-purple-300 shadow-sm'
                  : 'hover:bg-gray-100 border-transparent'
                  }`}
              >
                <span className="h-3 w-3 rounded bg-purple-600"></span>
                <span className={`font-semibold ${filters.raisedByCollector ? 'text-purple-800' : 'text-purple-700'}`}>
                  ★ {t('legend.collectorPriority')}
                </span>
                {filters.raisedByCollector && <span className="ml-1 text-purple-600">✓</span>}
              </div>

              <span className="text-gray-300">|</span>

              <div
                onClick={() =>
                  setFilters((prev) => ({
                    ...prev,
                    raisedByCitizen: !prev.raisedByCitizen,
                    raisedByCollector: !prev.raisedByCitizen ? false : prev.raisedByCollector,
                  }))
                }
                className={`flex items-center gap-1.5 cursor-pointer px-2 py-1 rounded transition-all border ${filters.raisedByCitizen
                  ? 'bg-blue-100 border-blue-300 shadow-sm'
                  : 'hover:bg-gray-100 border-transparent'
                  }`}
              >
                <span className="h-3 w-3 rounded border border-gray-300 bg-white"></span>
                <span className={filters.raisedByCitizen ? 'text-blue-800 font-medium' : ''}>
                  {t('legend.citizenRaised')}
                </span>
                {filters.raisedByCitizen && <span className="ml-1 text-blue-600">✓</span>}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-3">
                  <label className="text-sm font-medium text-gray-700">
                    <TranslatedText>Sort by :</TranslatedText>
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="date">{t('dashboard.table.date')}</option>
                    <option value="priority">{t('dashboard.table.priority')}</option>
                    <option value="status">{t('dashboard.table.status')}</option>
                  </select>
                  <button
                    onClick={() =>
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
                    }
                    className="rounded-lg border border-blue-200 px-3 py-2 text-sm text-blue-700 transition-colors hover:bg-blue-50"
                  >
                    {sortOrder === 'asc' ? '↑' : '↓'}
                  </button>
                </div>
              </div>

              {/* <div className="rounded-lg bg-gray-100 px-4 py-2 text-sm text-gray-500">
                Showing {startIndex + 1}-{Math.min(endIndex, filteredComplaints.length)} of {filteredComplaints.length} complaints
                {filteredComplaints.length !== complaints.length && ` (filtered from ${complaints.length} total)`}
              </div> */}
            </div>
          </div>

          {/* Color Legend */}
          {/* <div className="border-b border-t border-gray-200 bg-gray-50 px-6 py-3">
            <div className="flex items-center gap-4 text-xs text-gray-600">
              <span className="flex items-center gap-1.5">
                <span className="h-3 w-3 rounded bg-purple-600"></span>
                <span className="font-semibold text-purple-700">★ {t('legend.collectorPriority')}</span>
              </span>
              <span className="text-gray-400">|</span>
              <span className="flex items-center gap-1.5">
                <span className="h-3 w-3 rounded border border-gray-300 bg-white"></span>
                <span>{t('legend.citizenRaised')}</span>
              </span>
            </div>
          </div> */}


          {/* Complaints Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-8 py-5 text-left text-sm font-semibold uppercase tracking-wide text-gray-700">
                    {t('dashboard.table.complaintId')}
                  </th>
                  <th className="px-8 py-5 text-left text-sm font-semibold uppercase tracking-wide text-gray-700">
                    {t('dashboard.table.citizenDetails')}
                  </th>
                  <th className="px-8 py-5 text-left text-sm font-semibold uppercase tracking-wide text-gray-700">
                    {t('dashboard.table.priority')}
                  </th>
                  <th className="px-8 py-5 text-left text-sm font-semibold uppercase tracking-wide text-gray-700">
                    {t('dashboard.table.status')}
                  </th>
                  <th className="px-8 py-5 text-left text-sm font-semibold uppercase tracking-wide text-gray-700">
                    {t('dashboard.table.date')}
                  </th>
                  <th className="px-8 py-5 text-left text-sm font-semibold uppercase tracking-wide text-gray-700">
                    {t('dashboard.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {filteredComplaints.length > 0 ? (
                  filteredComplaints.map((complaint) => {

                    const formatStatusKey = (status) => {
                      if (!status) return '';
                      const normalizedStatus = status.replace(/\s+/g, '_');
                      const words = normalizedStatus.toLowerCase().split('_');
                      return `status.${words[0]}${words.slice(1).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('')}`;
                    };
                    const statusKey = formatStatusKey(complaint.status);

                    return (
                      <tr
                        key={complaint.id}
                        className={`border-b border-gray-50 transition-colors ${complaint.raisedBy === 'COLLECTOR'
                          ? 'bg-purple-100 hover:bg-purple-150 border-l-4 border-purple-600 shadow-sm'
                          : 'hover:bg-gray-50/50'
                          }`}
                      >
                        <td className="px-8 py-6 text-sm font-semibold">
                          <div className="flex items-center gap-2">
                            {complaint.raisedBy === 'COLLECTOR' && (
                              <span className="font-bold text-purple-700">★</span>
                            )}
                            <span
                              className={
                                complaint.raisedBy === 'COLLECTOR'
                                  ? 'text-purple-700 font-bold'
                                  : 'text-gray-900'
                              }
                            >
                              {complaint.complaintId}
                            </span>
                          </div>
                        </td>

                        <td className="px-8 py-6">
                          <div className="text-sm font-semibold text-gray-900">
                            <TranslatedText>{complaint.name}</TranslatedText>
                          </div>
                          <div className="mt-1 text-sm text-gray-500">
                            {formatNumberForLanguage(complaint.mobile, currentLanguage)}
                          </div>
                          <div className="text-sm text-gray-500">
                            <TranslatedText>{complaint.village}</TranslatedText>
                          </div>
                        </td>

                        <td className="px-8 py-6">
                          <span
                            className={`inline-flex px-3 py-2 text-xs font-semibold rounded-full whitespace-nowrap ${getPriorityColor(
                              complaint.priority,
                            )}`}
                          >
                            <TranslatedText>{`priority.${complaint.priority}`}</TranslatedText>
                          </span>
                        </td>

                        <td className="px-8 py-6">
                          <span
                            className={`inline-flex px-3 py-2 text-xs font-semibold rounded-full whitespace-nowrap ${getStatusColor(
                              complaint.status,
                            )}`}
                          >
                            <TranslatedText>{statusKey}</TranslatedText>
                          </span>
                        </td>

                        <td className="px-8 py-6 text-sm text-gray-600">
                          {formatNumberForLanguage(complaint.submittedDate, currentLanguage)}
                        </td>

                        <td className="px-8 py-6">
                          <div className="flex items-center space-x-3">
                            <select
                              onChange={(e) =>
                                handleStatusUpdate(complaint.id, e.target.value)
                              }
                              value={
                                DISPLAY_TO_ENUM_MAP[complaint.status] || complaint.status
                              }
                              disabled={
                                complaint.status === 'Resolved' ||
                                complaint.status === 'RESOLVED'
                              }
                              className={`border border-gray-200 rounded-lg pl-3 pr-8 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all appearance-none ${complaint.status === 'Resolved' ||
                                complaint.status === 'RESOLVED'
                                ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                : 'bg-white'
                                }`}
                            >
                              {/* <option value="All Status">{t('status.allStatus')}</option> */}
                              <option value="NEW">{t('status.new')}</option>
                              <option value="IN_PROGRESS">{t('status.inProgress')}</option>
                              <option value="RESOLVED">{t('status.resolved')}</option>
                              <option value="REJECTED">{t('status.rejected')}</option>
                              {(complaint.status === 'RESOLVED' ||
                                complaint.status === 'Resolved' ||
                                complaint.status === 'REJECTED' ||
                                complaint.status === 'Rejected') && (
                                  <option value="REOPEN">{t('status.reopen')}</option>
                                )}

                              <option value="PENDING">{t('status.pending')}</option>
                            </select>

                            <button
                              onClick={() => handleViewDetails(complaint)}
                              className="inline-flex items-center rounded-lg border border-blue-200 px-4 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-50 hover:text-blue-800"
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              {t('track.ViewDetails')}
                            </button>

                            <button
                              onClick={() => handleAssignClick(complaint)}
                              disabled={
                                complaint.status === 'Resolved' ||
                                complaint.status === 'RESOLVED' ||
                                complaint.status === 'Rejected' ||
                                complaint.status === 'REJECTED' ||
                                complaint.departmentName !== departmentName
                              }
                              className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors border ${complaint.status === 'Resolved' ||
                                complaint.status === 'RESOLVED' ||
                                complaint.status === 'Rejected' ||
                                complaint.status === 'REJECTED' ||
                                complaint.departmentName !== departmentName
                                ? 'text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed'
                                : 'text-orange-700 hover:text-orange-800 hover:bg-orange-50 border-orange-200'
                                }`}
                            >
                              <UserCheck className="mr-2 h-4 w-4" />
                              {t('actions.assign')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} className="px-8 py-16 text-center">
                      <div className="flex flex-col items-center justify-center space-y-3">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                          <FileText className="h-8 w-8 text-gray-400" />
                        </div>
                        <div>
                          <h3 className="mb-1 text-lg font-medium text-gray-900">
                            {t('emptyState.title')}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {t('emptyState.description')}
                          </p>
                          <p className="mt-1 text-xs text-gray-400">
                            {t('emptyState.suggestion')}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>

            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  {currentLanguage === 'mr' ? `पृष्ठ ${formatNumberForLanguage(currentPage, currentLanguage)} च ${formatNumberForLanguage(totalPages, currentLanguage)} (${formatNumberForLanguage(totalComplaints, currentLanguage)} तक्रार)` : `Page ${formatNumberForLanguage(currentPage, currentLanguage)} of ${formatNumberForLanguage(totalPages, currentLanguage)} (${formatNumberForLanguage(totalComplaints, currentLanguage)} complaints)`}
                </div>
                <div className="flex items-center gap-2">
                  {/* First Page */}
                  <button
                    onClick={goToFirstPage}
                    disabled={currentPage === 1}
                    className="rounded border border-gray-300 px-3 py-1 text-sm hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t('pagination.first')}
                  </button>

                  {/* Previous Page */}
                  <button
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                    className="flex items-center rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center gap-1">
                    {(() => {
                      const pages = [];
                      const showPages = 5;
                      let startPage = Math.max(1, currentPage - 2);
                      let endPage = Math.min(
                        totalPages,
                        startPage + showPages - 1
                      );

                      // Adjust start if we're near the end
                      if (endPage - startPage < showPages - 1) {
                        startPage = Math.max(1, endPage - showPages + 1);
                      }

                      // Add ellipsis at start
                      if (startPage > 1) {
                        pages.push(
                          <button
                            key={1}
                            onClick={() => goToPage(1)}
                            className={`px-3 py-1 border rounded text-sm ${currentPage === 1 ? 'bg-yellow-600 text-white border-yellow-600' : 'border-gray-300 hover:bg-gray-100'}`}
                          >
                            {formatNumberForLanguage(1, currentLanguage)}
                          </button>
                        );
                        if (startPage > 2) {
                          pages.push(
                            <span
                              key="start-ellipsis"
                              className="px-2 text-gray-500"
                            >
                              ...
                            </span>
                          );
                        }
                      }

                      // Add page numbers
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => goToPage(i)}
                            className={`px-3 py-1 border rounded text-sm ${currentPage === i ? 'bg-yellow-600 text-white border-yellow-600' : 'border-gray-300 hover:bg-gray-100'}`}
                          >
                            {formatNumberForLanguage(i, currentLanguage)}
                          </button>
                        );
                      }

                      // Add ellipsis at end
                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pages.push(
                            <span
                              key="end-ellipsis"
                              className="px-2 text-gray-500"
                            >
                              ...
                            </span>
                          );
                        }
                        pages.push(
                          <button
                            key={totalPages}
                            onClick={() => goToPage(totalPages)}
                            className={`px-3 py-1 border rounded text-sm ${currentPage === totalPages ? 'bg-yellow-600 text-white border-yellow-600' : 'border-gray-300 hover:bg-gray-100'}`}
                          >
                            {formatNumberForLanguage(totalPages, currentLanguage)}
                          </button>
                        );
                      }

                      return pages;
                    })()}
                  </div>

                  {/* Next Page */}
                  <button
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                    className="flex items-center rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>

                  {/* Last Page */}
                  <button
                    onClick={goToLastPage}
                    disabled={currentPage === totalPages}
                    className="rounded border border-gray-300 px-3 py-1 text-sm hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t('pagination.last')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Professional Modal */}
        {showDetailModal && selectedComplaint && (
          <div className="fixed inset-0 z-[9999] h-full w-full overflow-y-auto bg-black bg-opacity-50">
            <div className="relative top-4 mx-auto mb-8 w-11/12 max-w-6xl rounded-lg bg-white shadow-2xl sm:top-0 sm:mb-0 sm:h-full sm:w-full sm:rounded-none md:top-4 md:w-11/12">
              {/* Professional Header */}
              <div className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-4 sm:rounded-t-none md:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={handleCloseModal}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                    </button>
                    <div>
                      <h1 className="text-lg font-semibold text-gray-900">
                        {t('nav.dashboard')}
                      </h1>
                      <p className="text-sm text-gray-500">{t('dashboard.modal.complaint')}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    {/* <span className="text-sm text-gray-500">1 of 123</span> */}
                    <div className="flex items-center space-x-2">
                      {/* <button className="text-gray-400 hover:text-gray-600">
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <button className="text-gray-400 hover:text-gray-600">
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button> */}
                      <button
                        onClick={handleCloseModal}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Complaint Header */}
              <div
                className={`px-4 md:px-6 py-4 border-b border-gray-200 ${selectedComplaint.raisedBy === "COLLECTOR" ? "bg-purple-50" : ""
                  }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-4">
                      {selectedComplaint.raisedBy === "COLLECTOR" && (
                        <span className="text-xl font-bold text-purple-700">★</span>
                      )}

                      <h2
                        className={`text-xl font-semibold ${selectedComplaint.raisedBy === "COLLECTOR"
                          ? "text-purple-700"
                          : "text-gray-900"
                          }`}
                      >
                        {selectedComplaint.complaintId || "Water Supply Issue"}
                      </h2>

                      {selectedComplaint.raisedBy === "COLLECTOR" && (
                        <span className="inline-flex rounded-full bg-purple-600 px-3 py-1 text-xs font-bold text-white">
                          <TranslatedText>COLLECTOR PRIORITY</TranslatedText>
                        </span>
                      )}

                      {/* Priority Dropdown (Static) */}
                      <div className="flex items-center gap-2">
                        <label
                          htmlFor="priority"
                          className="text-sm font-medium text-gray-700"
                        >
                          {t('dashboard.table.priority')}:
                        </label>

                        <div className="relative">
                          <select
                            id="priority"
                            value={selectedComplaint.priority || "MEDIUM"}
                            onChange={(e) => handlePriorityUpdate(selectedComplaint.id, e.target.value)}
                            className={`text-sm font-semibold rounded-full px-4 py-1.5 border shadow-sm transition-all duration-300 appearance-none focus:outline-none pr-10 cursor-pointer w-full bg-transparent
        ${selectedComplaint.priority === "HIGH"
                                ? "bg-red-100 text-red-700 border-red-300 focus:ring-2 focus:ring-red-400"
                                : selectedComplaint.priority === "LOW"
                                  ? "bg-green-100 text-green-700 border-green-300 focus:ring-2 focus:ring-green-400"
                                  : "bg-yellow-100 text-yellow-700 border-yellow-300 focus:ring-2 focus:ring-yellow-400"
                              }`}
                          >
                            <option value="LOW" className="text-green-700">{t('priority.LOW')}</option>
                            <option value="MEDIUM" className="text-yellow-700">{t('priority.MEDIUM')}</option>
                            <option value="HIGH" className="text-red-700">{t('priority.HIGH')}</option>
                          </select>

                          {/* Custom dropdown arrow - make sure this is the ONLY arrow */}
                          <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-700">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </div>

                    </div>

                    <div className="mt-2 flex items-center space-x-4">
                      <span className="text-sm text-gray-500">
                        {selectedComplaint.submittedDate}{" "}
                        {calculateDaysAgo(selectedComplaint.submittedDate)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>



              {/* Status Progress */}
              <div className="border-b border-gray-200 px-4 py-4 md:px-6">
                <div className="mb-2 flex items-center justify-between">
                  <span
                    className={`px-2 py-1 text-sm font-medium rounded ${selectedComplaint.status === 'In Progress'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                      }`}
                  >
                    <TranslatedText>{formatStatusKeyVal(selectedComplaint.status)}</TranslatedText>
                  </span>
                  <span className="text-sm text-gray-500">
                    <TranslatedText>{t('Updated')}</TranslatedText>
                    <TranslatedText>{selectedComplaint.submittedDate}</TranslatedText>
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-gray-200">
                  <div
                    className={`h-2 rounded-full ${selectedComplaint.status === 'New Status'
                      ? 'w-1/4 bg-blue-500'
                      : selectedComplaint.status === 'In Progress'
                        ? 'w-3/4 bg-blue-500'
                        : selectedComplaint.status === 'Resolved' ||
                          selectedComplaint.status === 'RESOLVED'
                          ? 'w-full bg-green-500'
                          : selectedComplaint.status === 'Rejected'
                            ? 'w-full bg-red-500'
                            : 'w-1/4 bg-blue-500'
                      }`}
                  ></div>
                </div>
                <div className="mt-2 flex justify-between text-xs text-gray-500">
                  <span
                    className={
                      selectedComplaint.status === 'Submitted' ||
                        selectedComplaint.status === 'In Progress' ||
                        selectedComplaint.status === 'Resolved' ||
                        selectedComplaint.status === 'RESOLVED'
                        ? 'text-blue-600 font-medium'
                        : ''
                    }
                  >
                    ✓ <TranslatedText> Acknowledged</TranslatedText>
                  </span>
                  <span
                    className={
                      selectedComplaint.status === 'In Progress' ||
                        selectedComplaint.status === 'Resolved' ||
                        selectedComplaint.status === 'RESOLVED'
                        ? 'text-blue-600 font-medium'
                        : ''
                    }
                  >
                    {selectedComplaint.status === 'In Progress' ||
                      selectedComplaint.status === 'Resolved' ||
                      selectedComplaint.status === 'RESOLVED'
                      ? '✓'
                      : '◯'}{' '}
                    {t('status.inProgress')}
                  </span>
                  <span
                    className={
                      selectedComplaint.status === 'Resolved' ||
                        selectedComplaint.status === 'RESOLVED'
                        ? 'text-green-600 font-medium'
                        : ''
                    }
                  >
                    {selectedComplaint.status === 'Resolved' ||
                      selectedComplaint.status === 'RESOLVED'
                      ? '✓'
                      : '◯'}{' '}
                    {t('cards.resolved')}
                  </span>
                </div>
              </div>

              {/* Main Content Layout */}
              <div className="flex flex-col bg-gray-50 md:flex-row">
                {/* Left Content - Complainant Details */}
                <div className="w-full border-b border-r-0 border-gray-200 bg-white p-4 md:w-80 md:border-b-0 md:border-r md:p-6">
                  <div className="mb-6 text-center">
                    <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
                      <svg
                        className="h-8 w-8 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      <TranslatedText>{selectedComplaint.name}</TranslatedText>
                    </h3>

                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        {t('dashboard.table.complaintId')}
                      </label>
                      <p className="text-sm font-medium text-gray-900">
                        {selectedComplaint.complaintId}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        {t('home.contact.email')}
                      </label>
                      <p className="text-sm text-gray-900">
                        {selectedComplaint.email || 'citizen@example.com'}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        {t('home.contact.phone')}.
                      </label>
                      <p className="text-sm text-gray-900">
                        {formatNumberForLanguage(selectedComplaint.mobile, currentLanguage)}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        {t('track.details.address')}
                      </label>
                      <p className="max-h-28 overflow-y-auto break-all text-sm text-gray-900">
                        <TranslatedText>{selectedComplaint.address}</TranslatedText>
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        {t('complaint.form.village')}
                      </label>
                      <p className="text-sm text-gray-900">
                        <TranslatedText>{selectedComplaint.village}</TranslatedText>
                      </p>
                    </div>


                  </div>

                  {/* Status Update Dropdown */}
                  <div className="mt-6">
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      {t('dashboard.actions.update')}
                    </label>
                    <select
                      value={
                        DISPLAY_TO_ENUM_MAP[selectedComplaint.status] ||
                        selectedComplaint.status
                      }
                      onChange={(e) =>
                        handleStatusUpdate(selectedComplaint.id, e.target.value)
                      }
                      disabled={
                        selectedComplaint.status === 'Resolved' ||
                        selectedComplaint.status === 'RESOLVED'
                      }
                      className={`w-full border border-gray-200 rounded-lg pl-3 pr-8 py-3 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all appearance-none ${selectedComplaint.status === 'Resolved' ||
                        selectedComplaint.status === 'RESOLVED'
                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                        : 'bg-white'
                        }`}
                    >
                      <option value="NEW">{t('status.new')}</option>
                      <option value="PENDING">{t('status.pending')}</option>
                      <option value="IN_PROGRESS">{t('status.inProgress')}</option>
                      <option value="REOPEN">{t('status.reopen')}</option>
                      <option value="RESOLVED">{t('status.resolved')}</option>
                      <option value="REJECTED">{t('status.rejected')}</option>
                    </select>
                  </div>


                          {(selectedComplaint.status === 'RESOLVED' ||
                            selectedComplaint.status === 'Resolved' ||
                            selectedComplaint.status === 'REJECTED' ||
                            selectedComplaint.status === 'Rejected') && (
                              <div className="mt-4">
                                <button
                                  onClick={handleReopenComplaint}
                                  className="flex w-full items-center justify-center rounded-lg bg-purple-600 px-4 py-2 text-white transition-colors hover:bg-purple-700"
                                >
                                  <svg
                                    className="mr-2 h-4 w-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                    />
                                  </svg>
                                  {t('dashboard.table.reopenComplaint')}
                                </button>
                              </div>
                            )}


                  <button
                    onClick={handlePrint}
                    className="mt-4 flex w-full items-center justify-center rounded bg-gray-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700"
                  >
                    <Printer className="mr-2 h-4 w-4" />
                    {t('dashboard.table.printComplaint')}
                  </button>

                  {/* Assignment Information */}

                  {(selectedComplaint.assignmentReason ||
                    selectedComplaint.officerNotes ||
                    selectedComplaint.assignedTo) && (
                      <div className="mt-6 rounded-lg border border-indigo-200 bg-indigo-50 p-4">
                        <h4 className="mb-3 flex items-center text-sm font-medium text-indigo-900">
                          <UserCheck className="mr-2 h-4 w-4" />
                          {t('dashboard.modal.assignmentInfo')}
                        </h4>

                        <div className="space-y-3">
                          {/* Assigned To */}
                          {selectedComplaint.assignedTo && (
                            <div>
                              <label className="text-xs font-medium text-indigo-700">
                                {t('dashboard.modal.assignedTo')}
                              </label>
                              <p className="whitespace-pre-wrap break-words text-sm font-medium text-indigo-900">
                                <TranslatedText>{selectedComplaint.assignedTo}</TranslatedText>
                              </p>
                            </div>
                          )}

                          {/* Assignment Date */}
                          {selectedComplaint.assignedDate && (
                            <div>
                              <label className="text-xs font-medium text-indigo-700">
                                {t('dashboard.modal.assignmentDate')}
                              </label>
                              <p className="text-sm text-indigo-900">
                                {formatNumberForLanguage(selectedComplaint.assignedDate, currentLanguage)}
                              </p>
                            </div>
                          )}

                          {/* Assignment Reason */}
                          {selectedComplaint.officerNotes && (
                            <div>
                              <label className="text-xs font-medium text-indigo-700">
                                {t('admin.modal.officerNotes')}
                              </label>
                              <div className="max-h-32 overflow-y-auto">
                                <TranslatedText
                                  tag="p"
                                  className="whitespace-pre-line break-words text-sm text-indigo-900"
                                >
                                  {selectedComplaint.officerNotes}
                                </TranslatedText>
                              </div>
                            </div>

                          )}

                        </div>
                      </div>
                    )}

                </div>

                {/* Main Content - Complaint Details */}
                <div className="flex-1 space-y-4 p-4 md:space-y-6 md:p-6">
                  {/* Complaint Section */}
                  <div className="rounded-lg bg-white shadow">
                    <div className="border-b border-gray-200 px-4 py-4 md:px-6">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {t('dashboard.modal.complaint')}
                      </h3>
                    </div>
                    <div className="p-4 md:p-6">
                      <div className="max-h-64 w-full space-y-4 overflow-y-auto md:w-full lg:w-full">
                        <TranslatedText
                          tag="p"
                          className="overflow-y-auto break-all text-gray-700"
                        >
                          {selectedComplaint.description ||
                            'On May 12, 2024, I made a payment of ₹ 50,000 towards my loan account using debit card. This payment was processed through HDFC Bank and was given the transaction reference number 52522151HJ. Despite this, I have noticed that the payment has not been credited to my loan account.'}
                        </TranslatedText>



                        {selectedComplaint.category === 'Water Supply' && (
                          <div className="mt-4">
                            <h4 className="mb-2 font-medium text-gray-900">
                              {t('dashboard.modal.detailsOfIssue')}
                            </h4>
                            <ul className="list-inside list-disc space-y-1 text-gray-700">
                              <li>{t('dashboard.modal.location')}<TranslatedText> {selectedComplaint.village}</TranslatedText></li>
                              <li>
                                {t('dashboard.modal.reportedDate')} {formatNumberForLanguage(selectedComplaint.submittedDate, currentLanguage)}
                              </li>
                              <li>{t('dashboard.modal.contact')} {formatNumberForLanguage(selectedComplaint.mobile, currentLanguage)}</li>
                              <li>
                                {t('dashboard.modal.assignedDepartment')}{' '}
                                <TranslatedText>{selectedComplaint.assignedTo ||
                                  'Water Department'}</TranslatedText>
                              </li>
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Citizen Attachments Section */}

                  <div className="rounded-lg bg-white shadow">
                    <div className="border-b border-gray-200 px-4 py-4 md:px-6">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {t('dashboard.modal.citizenAttachments')}
                        </h3>
                        <span className="rounded bg-green-50 px-2 py-1 text-sm text-green-600">
                          {t('dashboard.modal.verified')}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="max-h-96 space-y-3 overflow-y-auto break-all">
                        {selectedComplaint.attachments_citizen &&
                          selectedComplaint.attachments_citizen.length > 0 ? (
                          selectedComplaint.attachments_citizen.map(
                            (attachment, index) => (
                              <div
                                key={index}
                                className="rounded-lg border border-gray-200 p-3"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                                      <Image className="h-5 w-5 text-blue-600" />
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">
                                        {`Citizen-${selectedComplaint.complaintId}-${index + 1}` ||
                                          `citizen_photo_${index + 1}.jpg`}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        {formatFileSizeWithLanguage(attachment.size, currentLanguage)}{' '}
                                        • {t('dashboard.modal.uploadedByCitizen')}
                                        {attachment.uploadedDate &&
                                          ` on ${new Date(attachment.uploadedDate).toLocaleDateString()}`}
                                      </p>


                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => handleViewImage(attachment, selectedComplaint.complaintId, false)}
                                      className="flex items-center gap-1 rounded px-3 py-1 text-sm font-medium text-blue-600 hover:bg-blue-50 hover:text-blue-800"
                                    >
                                      <Eye className="h-4 w-4" />
                                      {t('dashboard.modal.view')}
                                    </button>
                                    <button
                                      onClick={async () => {
                                        try {
                                          const normalizedEndpoint =
                                            attachment.url &&
                                              attachment.url.startsWith('/api')
                                              ? attachment.url.replace('/api', '')
                                              : attachment.url;
                                          const blob =
                                            await ApiService.getBlob(
                                              normalizedEndpoint
                                            );
                                          const objectUrl =
                                            URL.createObjectURL(blob);
                                          const ext =
                                            attachment.fileType &&
                                              attachment.fileType.includes('/')
                                              ? attachment.fileType.split('/')[1]
                                              : 'bin';
                                          const filename = `${selectedComplaint.complaintId}.${ext}`;

                                          const link =
                                            document.createElement('a');
                                          link.href = objectUrl;
                                          link.download = filename;
                                          document.body.appendChild(link);
                                          link.click();
                                          document.body.removeChild(link);
                                          URL.revokeObjectURL(objectUrl);
                                        } catch (error) {
                                          console.error(
                                            'Error downloading attachment:',
                                            error
                                          );
                                          alert('Error downloading attachment');
                                        }
                                      }}
                                      className="flex items-center gap-1 rounded px-3 py-1 text-sm font-medium text-blue-600 hover:bg-blue-50 hover:text-blue-800"
                                    >
                                      <Download className="h-4 w-4" />
                                      {t('dashboard.modal.download')}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )
                          )
                        ) : (
                          <div className="py-6 text-center">
                            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100">
                              <Image className="h-6 w-6 text-gray-400" />
                            </div>
                            <p className="text-sm text-gray-500">
                              {t('dashboard.modal.noPhotos')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Officer Resolution Attachments Section - Only show if resolved */}
                  {(
                    <div className="rounded-lg bg-white shadow">
                      <div className="border-b border-gray-200 px-4 py-4 md:px-6">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {t('dashboard.modal.officerResolutionPhotos')}
                          </h3>
                          <span className="rounded bg-green-50 px-2 py-1 text-sm text-green-600">
                            {t('dashboard.modal.resolutionEvidence')}
                          </span>
                        </div>
                      </div>
                      <div className="p-6">
                        <div className="max-h-96 space-y-3 overflow-y-auto break-all">
                          {selectedComplaint.attachments_officer &&
                            selectedComplaint.attachments_officer.length > 0 ? (
                            selectedComplaint.attachments_officer.map(
                              (attachment, index) => (
                                <div
                                  key={index}
                                  className="rounded-lg border border-gray-200 bg-green-50 p-3"
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
                                        <Image className="h-5 w-5 text-green-600" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">
                                          {`Officer-${selectedComplaint.complaintId}-${index + 1}` ||
                                            `resolution_photo_${index + 1}.jpg`}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                          {formatFileSizeWithLanguage(attachment.size, currentLanguage)}{' '}
                                          • {t('dashboard.modal.uploadedByOfficer')}
                                          {attachment.uploadedDate &&
                                            ` on ${new Date(attachment.uploadedDate).toLocaleDateString()}`}
                                        </p>

                                      </div>
                                    </div>
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => handleViewImage(attachment, selectedComplaint.complaintId, true)}
                                        className="flex items-center gap-1 rounded px-3 py-1 text-sm font-medium text-green-600 hover:bg-green-100 hover:text-green-800"
                                      >
                                        <Eye className="h-4 w-4" />
                                        {t('dashboard.modal.view')}
                                      </button>
                                      <button
                                        onClick={async () => {
                                          try {
                                            const normalizedEndpoint =
                                              attachment.url &&
                                                attachment.url.startsWith('/api')
                                                ? attachment.url.replace(
                                                  '/api',
                                                  ''
                                                )
                                                : attachment.url;
                                            const blob =
                                              await ApiService.getBlob(
                                                normalizedEndpoint
                                              );
                                            const objectUrl =
                                              URL.createObjectURL(blob);
                                            const ext =
                                              attachment.fileType &&
                                                attachment.fileType.includes('/')
                                                ? attachment.fileType.split(
                                                  '/'
                                                )[1]
                                                : 'bin';
                                            const filename = `${selectedComplaint.complaintId}_resolution.${ext}`;

                                            const link =
                                              document.createElement('a');
                                            link.href = objectUrl;
                                            link.download = filename;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                            URL.revokeObjectURL(objectUrl);
                                          } catch (error) {
                                            console.error(
                                              'Error downloading attachment:',
                                              error
                                            );
                                            alert('Error downloading attachment');
                                          }
                                        }}
                                        className="flex items-center gap-1 rounded px-3 py-1 text-sm font-medium text-green-600 hover:bg-green-100 hover:text-green-800"
                                      >
                                        <Download className="h-4 w-4" />
                                        {t('dashboard.modal.download')}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )
                            )
                          ) : (
                            <div className="py-6 text-center">
                              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100">
                                <Image className="h-6 w-6 text-gray-400" />
                              </div>
                              <p className="text-sm text-gray-500">
                                {t('dashboard.modal.noResolutionPhotos')}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Bottom Section - Timeline and Comments */}
              <div className="border-t border-gray-200 bg-gray-50">
                <div className="grid grid-cols-1 gap-4 p-4 md:gap-6 md:p-6 lg:grid-cols-2">

                  {/* Enhanced Timeline */}
                  <div className="rounded-lg bg-white shadow">
                    <div className="border-b border-gray-200 px-4 py-4 md:px-6">
                      <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.modal.actionTimeline')}</h3>
                    </div>

                    {/* Scrollable Timeline Section */}
                    <div className="max-h-96 overflow-y-auto rounded-xl border border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 p-6">
                      {/* max-h-96 ≈ shows around 4 items, adjust as needed */}

                      <div className="space-y-4">
                        {Array.isArray(selectedTimeLine) &&
                          selectedTimeLine.length > 0 ? (
                          (() => {
                            return selectedTimeLine.map((event, index) => {
                              const getActionDescription = (event) => {
                                if (event.action === 'STATUS_UPDATE' && event.note) {
                                  if (event.note.includes('status changed')) return event.note;
                                  if (event.note.includes('Status updated')) return event.note;
                                  return `Status updated: ${event.note}`;
                                }

                                // Map action codes to translation keys
                                const actionMap = {
                                  STATUS_UPDATE: t('dashboard.timeline.statusUpdated'),
                                  COMPLAINT_SUBMITTED: t('dashboard.timeline.complaintSubmitted'),
                                  COMPLAINT_ASSIGNED: t('dashboard.timeline.complaintAssigned'),
                                  COMPLAINT_RESOLVED: t('dashboard.timeline.complaintResolved'),
                                  COMPLAINT_REJECTED: t('dashboard.timeline.complaintRejected'),
                                  COMPLAINT_REOPENED: t('dashboard.timeline.complaintReopened'),
                                  COMMENT_ADDED: t('dashboard.timeline.commentAdded'),
                                  DOCUMENT_UPLOADED: t('dashboard.timeline.documentUploaded'),
                                };

                                // If action is a code, use the map
                                if (actionMap[event.action]) {
                                  return actionMap[event.action];
                                }

                                // Map text-based actions to translation keys for dynamic content from API
                                const textActionMap = {
                                  'Priority Updated': t('dashboard.timeline.priorityUpdated'),
                                  'Status Updated': t('dashboard.timeline.statusUpdated'),
                                  'Complaint Submitted': t('dashboard.timeline.complaintSubmitted'),
                                  'Complaint Assigned': t('dashboard.timeline.complaintAssigned'),
                                  'Complaint Resolved': t('dashboard.timeline.complaintResolved'),
                                  'Complaint Reopened': t('dashboard.timeline.complaintReopened'),
                                  'Complaint Rejected': t('dashboard.timeline.complaintRejected'),
                                  'Comment Added': t('dashboard.timeline.commentAdded'),
                                  'Document Uploaded': t('dashboard.timeline.documentUploaded'),
                                };

                                // Check if the action text matches any known patterns
                                return textActionMap[event.action] ||
                                  (textActionMap[event.action.toLowerCase()] ? textActionMap[event.action.toLowerCase()] :
                                    event.action || t('dashboard.timeline.actionTaken'));
                              };

                              const getOfficerInfo = (event) => {
                                if (event.officer && event.officer !== 'N/A' && event.officer !== '')
                                  return event.officer;
                                if (event.updatedBy) return event.updatedBy;
                                if (event.assignedTo) return event.assignedTo;
                                return 'System';
                              };

                              const getRaisedByTranslation = (raisedByValue) => {
                                if (!raisedByValue) return t('dashboard.common.citizen');
                                const value = raisedByValue.toLowerCase();
                                if (value === 'citizen') return t('raisedBy.citizen');
                                if (value === 'collector') return t('raisedBy.collector');
                                if (value === 'officer') return t('raisedBy.officer');
                                if (value === 'admin') return t('raisedBy.admin');
                                return raisedByValue;
                              };

                              return (
                                <div key={index} className="flex items-start">
                                  <div className="flex items-center">
                                    <div
                                      className="h-4 w-4 rounded-full"
                                      style={{ backgroundColor: '#B8860B' }}
                                    ></div>
                                    {index != 0 && (
                                      <div
                                        className="ml-2 mt-2 h-12 w-px"
                                        style={{ backgroundColor: '#B8860B' }}
                                      ></div>
                                    )}
                                  </div>
                                  <div className="ml-4 flex-1">
                                    <div className="flex items-start justify-between">
                                      <div className="mr-4 flex-1 rounded-lg border border-green-200 bg-white p-3">
                                        <h4 className="mb-1 font-semibold text-gray-900">
                                          <TranslatedText>{getActionDescription(event)}</TranslatedText>
                                        </h4>
                                        {/* {event.note && event.action !== 'STATUS_UPDATE' && (
                        <p className="mb-2 text-sm text-gray-700">
                          {event.note}
                        </p>
                      )} */}
                                        {event.description && (
                                          <TranslatedText
                                            tag="p"
                                            className="mb-2 max-h-40 overflow-y-auto break-words text-sm text-gray-700"
                                          >
                                            {event.description}
                                          </TranslatedText>
                                        )}
                                        <p
                                          className="text-xs font-medium"
                                          style={{ color: '#B8860B' }}
                                        >
                                          {t('dashboard.modal.by')} <TranslatedText>{event.officerName || getRaisedByTranslation(selectedComplaint.raisedBy)}</TranslatedText>
                                        </p>
                                        {/* ✅ Added scrollable + breakable text container */}
                                        {event.note && (
                                          <div className="w-600 lg:w-4/4 max-h-64 space-y-4 overflow-y-auto md:w-full">
                                            <TranslatedText
                                              tag="p"
                                              className="break-words text-gray-700"
                                            >
                                              {event.note}
                                            </TranslatedText>
                                          </div>
                                        )}


                                      </div>
                                      <div className="whitespace-nowrap rounded border border-green-200 bg-white px-2 py-1 text-xs text-gray-500">
                                        {formatNumberForLanguage(event.date || event.timestamp || event.createdAt, currentLanguage)}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            });
                          })()
                        ) : (
                          <p className="text-sm text-gray-600">{t('dashboard.modal.noTimeline')}</p>
                        )}
                      </div>
                    </div>
                  </div>


                  {/* Comments */}
                  <div className="rounded-lg bg-white shadow">
                    <div className="border-b border-gray-200 px-4 py-4 md:px-6">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {t('dashboard.modal.commentsUpdates')}
                      </h3>
                    </div>
                    <div className="p-4 md:p-6">
                      <div className="mb-6 max-h-80 space-y-4 overflow-y-auto">
                        {[...comments].reverse().map((comment) => (
                          <div key={comment.id} className="flex items-start space-x-3">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center ${comment.visibility === "PUBLIC" ? "bg-green-100" : "bg-orange-100"
                                }`}
                            >
                              <span
                                className={`text-xs font-semibold ${comment.visibility === "PUBLIC"
                                  ? "text-green-600"
                                  : "text-orange-600"
                                  }`}
                              >
                                {comment.visibility === "PUBLIC" ? "P" : "I"}
                              </span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <p className="text-sm font-medium text-gray-900">
                                    <TranslatedText text={comment.commentBy} />
                                  </p>
                                  <span
                                    className={`text-xs px-2 py-1 rounded-full ${comment.visibility.toLowerCase() === "public"
                                      ? "bg-green-100 text-green-700"
                                      : "bg-orange-100 text-orange-700"
                                      }`}
                                  >
                                    {comment.visibility.toLowerCase() === "public"
                                      ? t('dashboard.modal.public')
                                      : t('dashboard.modal.internal')}
                                  </span>
                                </div>
                                <span className="text-xs text-gray-500">
                                  {formatDateTime(comment.commentedAt)}
                                </span>
                              </div>

                              {/* Added scrollable + breakable text wrapper here */}
                              <div className="w-600 lg:w-4/4 max-h-64 space-y-4 overflow-y-auto md:w-full">
                                <TranslatedText
                                  tag="p"
                                  className="mt-1 break-all text-gray-700"
                                >
                                  {comment.comment}
                                </TranslatedText>
                              </div>
                            </div>
                          </div>
                        ))}

                        {comments.length === 0 && (
                          <div className="py-8 text-center">
                            <MessageCircle className="mx-auto mb-3 h-12 w-12 text-gray-300" />
                            <p className="text-sm font-medium text-gray-900">{t('dashboard.modal.noComments')}</p>
                            <p className="mt-1 text-xs text-gray-500">
                              {t('dashboard.modal.addFirstComment')}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="border-t border-gray-200 pt-6">
                        <div className="mb-4 flex items-center gap-3">
                          <button
                            onClick={() => setCommentType("PUBLIC")}
                            className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${commentType === "PUBLIC"
                              ? "bg-green-100 text-green-800 ring-1 ring-green-300"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                              }`}
                          >
                            <span>{t('dashboard.modal.public')}</span>
                          </button>
                          <button
                            onClick={() => setCommentType("INTERNAL")}
                            className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${commentType === "INTERNAL"
                              ? "bg-orange-100 text-orange-800 ring-1 ring-orange-300"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                              }`}
                          >
                            <span>{t('dashboard.modal.internal')}</span>
                          </button>
                        </div>

                        <div className="flex gap-3">
                          <textarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder={commentType === "PUBLIC" ? t('dashboard.comments.addPublicComment') : t('dashboard.comments.addInternalComment')}
                            className="flex-1 resize-none rounded-lg border border-gray-300 p-4 text-sm transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                            rows="3"
                          />
                          <button
                            onClick={handleAddComment}
                            disabled={!newComment.trim()}
                            className={`px-6 py-3 text-white text-sm font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed ${commentType === "PUBLIC"
                              ? "bg-green-600 hover:bg-green-700"
                              : "bg-orange-600 hover:bg-orange-700"
                              }`}
                          >
                            {t('dashboard.comments.post')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        )}

        {/* Resolution Modal */}

        {showResolutionModal && resolutionComplaint && (
          <div className="fixed inset-0 z-[9999] h-full w-full overflow-y-auto bg-black bg-opacity-50 backdrop-blur-sm">
            <div className="relative top-4 mx-auto mb-8 w-11/12 max-w-3xl rounded-lg bg-white shadow-2xl">
              {/* Header */}
              <div className="border-b border-gray-200 bg-gradient-to-r from-green-50 to-green-100 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500">
                      <CheckCircle className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {t('resolutionModal.title')}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {t('resolutionModal.idLabel')}: {resolutionComplaint.complaintId}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowResolutionModal(false);
                      setResolutionComplaint(null);
                      setResolutionComment('');
                      setResolutionFiles([]);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="space-y-6">
                  {/* Mandatory Closing Comment */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      {t('resolutionModal.closingComment.required')}
                    </label>
                    <textarea
                      value={resolutionComment}
                      onChange={(e) => setResolutionComment(e.target.value)}
                      placeholder={t('resolutionModal.closingComment.placeholder')}
                      className="w-full resize-none rounded-lg border border-gray-300 px-3 py-3 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-500"
                      rows={4}
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {t('resolutionModal.closingComment.helpText')}
                    </p>
                  </div>

                  {/* Optional Photo Upload */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      {t('resolutionModal.photos.label')}
                    </label>
                    <div className="rounded-lg border-2 border-dashed border-gray-300 p-4 text-center transition-colors hover:border-gray-400">
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={(e) =>
                          setResolutionFiles([...resolutionFiles, ...Array.from(e.target.files)])
                        }
                        className="hidden"
                        id="resolution-files"
                      />
                      <label
                        htmlFor="resolution-files"
                        className="cursor-pointer"
                      >
                        <Upload className="mx-auto mb-2 h-8 w-8 text-gray-400" />
                        <p className="text-sm text-gray-600">
                          {t('resolutionModal.photos.uploadPrompt')}
                        </p>
                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-3 border-t border-gray-100 pt-4">
                          {(resolutionComplaint.status === 'RESOLVED' ||
                            resolutionComplaint.status === 'Resolved' || resolutionComplaint.status === 'REJECTED' || resolutionComplaint.status === 'Rejected') && (
                              <button
                                onClick={() => handleStatusUpdate(resolutionComplaint.id, 'REOPEN')}
                                className="flex-1 rounded-lg bg-purple-600 px-4 py-2 text-white transition-colors hover:bg-purple-700"
                              >
                                {t('actions.reopen')}
                              </button>
                            )}
                        </div>
                        <p className="text-xs text-gray-500">
                          {t('resolutionModal.photos.fileTypes')}
                        </p>
                      </label>
                    </div>
                    {resolutionFiles.length > 0 && (
                      <div className="mt-3">
                        <p className="mb-2 text-sm font-medium text-gray-700">
                          {t('resolutionModal.photos.selectedLabel')}
                        </p>
                        <div className="space-y-1">
                          {resolutionFiles.map((file, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between rounded bg-gray-50 p-2"
                            >
                              <span className="text-sm text-gray-600">
                                {file.name}
                              </span>
                              <button
                                onClick={() =>
                                  setResolutionFiles((prev) =>
                                    prev.filter((_, i) => i !== index)
                                  )
                                }
                                className="text-red-500 hover:text-red-700"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Warning */}
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">
                          {t('resolutionModal.warning.title')}
                        </p>
                        <p className="text-sm text-amber-700">
                          {t('resolutionModal.warning.message')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
                <button
                  onClick={() => {
                    setShowResolutionModal(false);
                    setResolutionComplaint(null);
                    setResolutionComment('');
                    setResolutionFiles([]);
                  }}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
                >
                  {t('resolutionModal.buttons.cancel')}
                </button>
                <button
                  onClick={() => {
                    handleResolveComplaint();
                    handleUploadResolutionPhotos(resolutionComplaint.id);
                  }}
                  disabled={!resolutionComment.trim() || resolvingComplaint}
                  className="flex items-center gap-2 rounded-lg bg-green-500 px-6 py-2 text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {resolvingComplaint ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      {t('resolutionModal.buttons.resolving')}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      {t('resolutionModal.buttons.resolve')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Assign Complaint Modal */}
        {showAssignModal && assignComplaint && (
          <AssignComplaintPage
            complaint={assignComplaint}
            onClose={handleAssignClose}
            onAssignSuccess={handleAssignSuccess}
          // onAssign={handleAssign}
          />
        )}

        {/* Image Preview Modal */}
        {showImageModal && selectedImage && (
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-75 p-4"
            onClick={handleCloseImageModal}
          >
            <div
              className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between border-b border-gray-200 p-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  <TranslatedText>{selectedImage.filename}</TranslatedText>
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleDownloadFromModal}
                    className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
                  >
                    <Download className="h-4 w-4" />
                    {t('dashboard.modal.download')}
                  </button>
                  <button
                    onClick={handleCloseImageModal}
                    className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                    aria-label="Close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Modal Body - Image Preview */}
              <div className="flex flex-1 items-center justify-center overflow-auto bg-gray-100 p-4">
                <img
                  src={selectedImage.url}
                  alt={selectedImage.filename}
                  className="max-w-full max-h-[calc(90vh-120px)] object-contain rounded-lg"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EFailed to load image%3C/text%3E%3C/svg%3E';
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DashboardPage;
